// NGRP Organisational Constitution — Blueprint content.
// Authored by the party leadership. Rendered on /constitution.

export const CONSTITUTION = {
  title: "NGRP Organisational Constitution — Blueprint",
  subtitle:
    "One important principle: keep the party organisation separate from elected-government institutions. NGRP committees organise around geographies without presenting themselves as government bodies.",

  supreme_clause:
    "\u201cNo individual, including the Founder, President or any other office-bearer, shall be above the Constitution, ethical standards or duly established democratic institutions of the Party.\u201d",

  foundational: {
    vision:
      "Build an India where government is transparent, efficient, accountable, technology-enabled and citizen-centric.",
    philosophy: "Knowledge \u2192 Integrity \u2192 Service \u2192 Performance \u2192 Public Trust \u2192 Leadership",
    reject:
      "NGRP rejects the idea that political leadership is determined by wealth, family background, caste/community influence or personal connections. At the same time, academic qualifications alone should never determine leadership.",
    test: "Competence + Character + Ground Work + Public Acceptance + Performance",
  },

  levels: [
    "Citizen / Member",
    "Booth / Village / Ward Committee",
    "Cluster Committee",
    "Taluka / Municipal Committee",
    "District Committee",
    "Assembly Constituency Committee",
    "Lok Sabha Constituency Committee",
    "State Council",
    "National Council",
  ],

  village_committee: {
    heading: "Village / Ward Committee — foundation of NGRP",
    size: "~11 active office-bearers per local unit",
    responsibilities: [
      "Local Coordinator",
      "Deputy Coordinator",
      "Organisation Secretary",
      "Citizen-Services Coordinator",
      "Women / Youth Representation",
      "Farmer / Rural-Economy Representation",
      "Digital / Data Responsibility",
      "Other locally-relevant roles",
    ],
    purpose:
      "The unit\u2019s job is not merely election campaigning. Every local unit maintains an NGRP Public Issues Register.",
    register_fields: [
      "Issue",
      "Department",
      "Date raised",
      "Action taken",
      "Current status",
      "Resolution",
    ],
    issue_examples: [
      "Drinking water",
      "Roads",
      "Streetlights",
      "School facilities",
      "Healthcare access",
      "Government certificates",
      "Agricultural services",
      "Delayed public works",
    ],
    outcome: "This becomes NGRP\u2019s grassroots governance database.",
  },

  cluster_taluka: {
    heading: "Cluster and Taluka Structure",
    body:
      "A cluster coordinates ~5\u201310 neighbouring village/ward units where geography makes it useful. The Taluka organisation then coordinates the clusters \u2014 with a small operational team, not 50 ceremonial office-bearers.",
    functions: [
      "Organisation",
      "Public Grievances",
      "Policy",
      "Communications",
      "Legal / Compliance",
      "Training",
      "Women / Youth participation",
      "Local Development",
    ],
    monthly_report_example: [
      { label: "Village units functioning", value: "23" },
      { label: "Citizen issues recorded", value: "418" },
      { label: "Resolved / assisted", value: "167" },
      { label: "Under departmental consideration", value: "92" },
      { label: "Requiring further action", value: "159" },
    ],
    tagline: "This creates measurable politics.",
  },

  district_council: {
    heading: "District Council",
    body:
      "The District Council is a serious management layer with ~21\u201331 functional members, depending on district size, publishing a periodic District Governance Report.",
    functions: [
      "Organisation",
      "Policy / Research",
      "Legal Affairs",
      "Finance / Compliance",
      "Communications",
      "Digital Operations",
      "Membership / Training",
      "Agriculture / Rural Development",
      "Industry / Employment",
      "Infrastructure",
      "Education",
      "Health",
      "Women",
      "Youth",
    ],
  },

  assembly_constituency: {
    heading: "Assembly Constituency Organisation",
    body:
      "A permanent Constituency Governance Team. Purpose extends well beyond elections \u2014 maintains a constituency development database and prevents last-minute parachute candidates from outranking members who have served for years.",
    database_areas: [
      "Major villages / wards",
      "Infrastructure",
      "Water",
      "Schools",
      "Hospitals",
      "Agriculture",
      "Employment",
      "Public Transport",
      "Citizen-Service Problems",
    ],
  },

  loksabha_council: {
    heading: "Lok Sabha Constituency Council",
    body:
      "Coordinates the Assembly-level teams within the constituency and focuses on larger issues \u2014 regional infrastructure, railways, national highways, major irrigation, industrial development, environmental issues and Union-government programmes.",
    principle:
      "Local problems should be solved locally wherever possible. Higher committees intervene only when higher authority is required.",
  },

  state_council: {
    heading: "Maharashtra State Council",
    intro:
      "Political leadership is deliberately separated from executive management. No single person is the bottleneck for every decision.",
    roles: [
      { title: "State President", role: "Political leadership" },
      { title: "Working President / Executive Chair", role: "Organisational execution" },
      { title: "General Secretary \u2014 Organisation", role: "Expansion and cadre" },
      { title: "General Secretary \u2014 Policy", role: "Manifesto and research" },
      { title: "Treasurer", role: "Financial governance" },
      { title: "Legal & Compliance Head", role: "" },
      { title: "Training & Leadership Head", role: "" },
      { title: "Technology / Data Head", role: "" },
      { title: "Communications Head", role: "" },
    ],
    subject_councils: [
      "Agriculture",
      "Industry",
      "Infrastructure",
      "Education",
      "Healthcare",
      "Energy / Environment",
      "Urban Governance",
      "Rural Development",
      "Public Finance",
    ],
  },

  national_structure: {
    heading: "National Structure",
    chain: [
      "National Convention",
      "National Council",
      "National Executive Committee",
      "President + Executive Leadership",
    ],
    principle:
      "The National Convention is the highest representative party forum \u2014 not the founder personally possessing unlimited authority. That institutional distinction will matter as NGRP grows.",
  },

  terms: {
    heading: "Leadership Terms",
    body:
      "Fixed terms (e.g. 3 years), subject to the final constitution and applicable election law. No unlimited tenure. Attendance and performance requirements: an office-bearer who repeatedly fails to participate does not retain a title indefinitely.",
  },

  academy: {
    heading: "NGRP School of Public Leadership",
    body:
      "An independent training platform within the party \u2014 open not only to UPSC/MPSC candidates but also engineers, doctors, lawyers, teachers, entrepreneurs, farmers, researchers and other capable citizens.",
    curriculum: [
      "Constitution",
      "Panchayati Raj",
      "Municipal Governance",
      "Public Finance",
      "Economics",
      "Legislation",
      "Procurement",
      "Infrastructure",
      "Agriculture",
      "Technology",
      "Ethics",
      "Public Communication",
    ],
    field_rule:
      "Training alone doesn\u2019t produce leadership. Every participant completes a field assignment: adopt one village/ward, identify 25 issues, classify them by government authority, develop solutions, engage the relevant administration and document results.",
  },

  candidate_selection: {
    heading: "Candidate-Selection System",
    intro:
      "One senior leader does not distribute tickets. A structured multi-stage pipeline evaluates candidates.",
    pipeline: [
      "Open application",
      "Eligibility / background checks",
      "Integrity review",
      "Public-service record",
      "Policy / constitutional assessment",
      "Constituency performance",
      "Local feedback",
      "Structured interview",
      "Independent selection panel",
      "Final nomination",
    ],
    scoring: [
      { criterion: "Integrity / background", weight: 20 },
      { criterion: "Public-service record", weight: 20 },
      { criterion: "Constituency work", weight: 20 },
      { criterion: "Governance / policy competence", weight: 15 },
      { criterion: "Public acceptance", weight: 15 },
      { criterion: "Communication / leadership", weight: 10 },
    ],
    note: "Wealth or personal fundraising capacity is not used as a candidate-selection score.",
  },

  ethics: {
    heading: "Anti-Corruption Architecture \u2014 Ethics & Integrity Commission",
    body:
      "Zero corruption isn\u2019t just an external promise \u2014 it\u2019s an internal safeguard. An independent NGRP Ethics & Integrity Commission investigates complaints against office-bearers with due process.",
    process: [
      "Complaint",
      "Preliminary assessment",
      "Investigation",
      "Opportunity to respond",
      "Reasoned decision",
      "Appeal",
    ],
    disclosures:
      "Senior officials and candidates make legally required disclosures and, where appropriate, additional voluntary transparency disclosures.",
  },

  finance: {
    heading: "Financial Transparency",
    body:
      "Formal accounting from the first rupee. No informal cash structures. No undocumented district-level collections.",
    chain: [
      "Banking",
      "Accounting",
      "Donor compliance",
      "Expenditure authorization",
      "Audit",
      "Statutory reporting",
    ],
    note: "Voluntarily publish understandable financial summaries beyond minimum legal disclosure requirements.",
  },

  internal_democracy: {
    heading: "Internal Democracy",
    body:
      "Important positions gradually move from founder appointments to internal elections once membership is verified.",
    sequence: [
      "Founding / Interim Organisation",
      "Membership Verification",
      "Local Organisational Elections",
      "District Elections",
      "State Convention",
    ],
    warning: "Temporary appointments have a habit of becoming permanent \u2014 attach an expiry date.",
  },

  membership: {
    heading: "Membership Categories",
    tiers: [
      { name: "Supporter", body: "Supports NGRP but is not part of formal internal decision-making." },
      { name: "Primary Member", body: "Formally enrolled under party rules." },
      { name: "Active Member", body: "Meets participation and training requirements." },
      { name: "Leadership Cadre", body: "Eligible for significant organisational responsibility after demonstrated work." },
    ],
    note:
      "Membership requires individual voluntary consent. UPSC/MPSC students are not automatically made members.",
  },

  representation: {
    heading: "Women and Youth Participation",
    body:
      "Rather than creating wings that have no power, NGRP guarantees meaningful representation for women, young people and underrepresented communities within the main decision-making structure \u2014 consistent with applicable law and the party\u2019s constitutional commitments.",
  },

  technology: {
    heading: "Technology Architecture",
    body:
      "A secure NGRP organisational platform. Local coordinators see their jurisdiction; district leadership sees aggregated district data; state leadership sees statewide trends. Citizens see only what is appropriate for public disclosure. Members\u2019 personal information is never publicly exposed.",
    modules: [
      "Verified Membership",
      "Organisational Hierarchy",
      "Training",
      "Public Grievances",
      "Volunteer Opportunities",
      "Events",
      "Policy Suggestions",
      "Performance Dashboard",
      "Financial Disclosures",
    ],
  },

  performance_review: {
    heading: "Performance-Based Promotion",
    body:
      "Every organisational leader has a quarterly scorecard \u2014 audited numbers, not manufactured memberships.",
    factors: [
      "Organisation building",
      "Attendance",
      "Training",
      "Public-service activity",
      "Issue resolution",
      "Reporting",
      "Ethical compliance",
    ],
  },

  first_target: {
    heading: "First Organisational Target",
    intro:
      "NGRP does not announce \u201cWe will contest every seat.\u201d It builds milestones.",
    stages: [
      "100 credible founders",
      "1,000 trained organisers",
      "10,000 active governance reformers",
      "Functional village/ward organisations in selected pilot districts",
      "Taluka and district structures",
      "Assembly constituency organisations",
      "Statewide organisation",
      "Candidate deployment based on organisational strength",
    ],
    closing: "Maharashtra becomes the proof-of-concept state for national expansion.",
  },

  eci_note:
    "Before formal registration or collection of political contributions, the final constitution, membership rules, party name/symbol matters, accounting framework and political-contribution procedures will be reviewed against current Election Commission of India requirements and applicable Indian election, tax and financial-disclosure laws.",
};
